import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import * as d3 from 'd3';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import data from './goldmedalistsinfo.json';
import Tooltip from './Tooltip';
import ImageScroll from './ImagesScroll';

const ResidenceTiers = () => {
  const svgRef = useRef();
  const containerRef = useRef();
  const scrollTriggerRef = useRef(null);

  const [tooltipContent, setTooltipContent] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [hovered, setHovered] = useState(null);

  const sex = ['女', '男'];

  // Memoize color scale
  const color = useMemo(() => 
    d3.scaleOrdinal()
      .domain(['男', '女'])
      .range(['#71DF96', '#BB96FF']),
    []
  );

  // Memoize processed data
  const processedData = useMemo(() => {
    const HKOverseasData = data.map(d => ({
      ...d,
      hometownTier: d.hometownTier === '海外' || d.hometownTier === '香港' ? '海外及香港' : d.hometownTier,
      currentTier: d.currentTier === '海外' || d.currentTier === '香港' ? '海外及香港' : d.currentTier
    }));
    return HKOverseasData.filter(d => d.currentTier !== undefined && d.currentTier !== '不详');
  }, []);

  const debounce = useCallback((func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }, []);

  const drawChart = useCallback(() => {
    if (!svgRef.current || !containerRef.current) return;

    // Clear existing content
    d3.select(svgRef.current).selectAll('*').remove();

    gsap.registerPlugin(ScrollTrigger);
    
    const isMobile = window.innerWidth <= 768;
    const container = d3.select(containerRef.current);
    const svg = d3.select(svgRef.current);
    
    // Setup dimensions
    const margin = { top: 55, right: -25, bottom: 50, left: 80 };
    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;
    const width = isMobile ? 
      svgRef.current.getBoundingClientRect().width * 0.84 : 
      containerWidth - margin.left - margin.right;
    const height = isMobile ? 600 : containerHeight - margin.top - margin.bottom;

    // Setup SVG
    svg
      .attr('viewBox', `0 0 ${containerWidth} ${containerHeight}`)
      .attr('preserveAspectRatio', 'xMinYMin meet')
      .attr('width', '100%')
      .attr('height', '100%');

    const svgGroup = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Setup scales
    const tierLabels = [
      '海外及香港', '一线城市', '新一线城市', '二线城市', '三线城市', '四线城市', '五线城市', '县级市及以下'
    ];

    const x = d3.scalePoint()
      .domain(tierLabels)
      .range([0, width])
      .padding(0.5);

    const y = d3.scaleBand()
      .domain(processedData.map(d => d.sportcn))
      .range([0, height])
      .paddingInner(0.5)
      .paddingOuter(0.5);

    

    // // Background highlight for top sports
    // const topSports = y.domain().slice(0, 12);
    // const topHeight = y.bandwidth() * topSports.length + 6;

    // const backgroundRect = svgGroup.append('rect')
    //   .attr('class', 'row-highlight')
    //   .attr('x', -margin.left + 10)
    //   .attr('y', y(topSports[0]))
    //   .attr('width', width + margin.left + 10)
    //   .attr('height', topHeight)
    //   .attr('fill', 'white')
    //   .attr('opacity', 0)
    //   .attr('z-index', -100);

    ///new
    // Background highlight for top sports
    const topSports = y.domain().slice(0, 11);
    const topHeight = y.bandwidth() * topSports.length + 6;

    const backgroundRect = svgGroup.append('rect')
      .attr('class', 'row-highlight')
      .attr('x', -margin.left + 10)
      .attr('y', y(topSports[0]))
      .attr('width', width + margin.left + 10)
      .attr('height', topHeight)
      .attr('fill', 'white')
      .attr('opacity', 0)
      .attr('z-index', -10);

    const gridGroup = svgGroup.append('g')
      .attr('class', 'grid-layer');

    // Add axes
    const yAxis = d3.axisLeft(y)
      .tickSizeInner(-width)
      .tickSizeOuter(0)
      .tickPadding(5);

    gridGroup.append('g')
      .attr('class', 'y-axis')
      .call(yAxis)
      .call(g => {
        g.selectAll('.tick line')
          .attr('class', 'grid-lines')
          .attr('x1', 2);
        g.select('.domain').remove();
      });

    const xAxis = d3.axisBottom(x)
      .tickSizeInner(-height)
      .tickSizeOuter(0)
      .tickPadding(10);

    gridGroup.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${height})`)
      .call(xAxis)
      .call(g => {
        g.selectAll('.tick line')
          .attr('class', 'grid-lines')
          .attr('transform', `translate(${x.step() / 2},0)`);
      });

    // Calculate jitter
    const jitterAmountx = isMobile ? 40 : 85;
    const jitterAmounty = isMobile ? 18 : 8;

    // Prepare data with initial and final positions
    processedData.forEach(d => {
      d.initialX = x(d.hometownTier) + (Math.random() - 0.5) * jitterAmountx;
      d.initialY = y(d.sportcn) + (Math.random() - 0.5) * jitterAmounty + 7;
      d.finalX = x(d.currentTier) + (Math.random() - 0.5) * jitterAmountx;
      d.finalY = y(d.sportcn) + (Math.random() - 0.5) * jitterAmounty + 7;
    });

    // Add data points
    const circles = svgGroup.selectAll('.hometown-dot')
      .data(processedData)
      .join('circle')
      .attr('class', 'hometown-dot')
      .attr('cx', d => d.initialX)
      .attr('cy', d => d.initialY)
      .attr('r', 5)
      .style('fill', d => color(d.sex))
      .style('stroke', d => color(d.sex))
      .attr('fill-opacity', 0.4)
      .on('mouseover', (event, d) => {
        setTooltipContent(`
          <div class="tooltip__content">
            <span class="tooltip__title">
              <b>${d.namecn}</b> <span style="color:${color(d.sex)}">(${d.sexcn})</span></br>
              <b>${d.sportcn}</b></br>
            </span>
            <span class="tooltip__details">
              家乡：${d.hometown}</br>
              现居：${d.currentResidence}
            </span>
          </div>
        `);
        setTooltipPosition({ x: event.pageX + 10, y: event.pageY - 28 });
        setHovered(event.target);
      })
      .on('mouseout', () => {
        setTooltipContent(null);
        setHovered(null);
      });

    // Create ScrollTrigger
    if (scrollTriggerRef.current) {
      scrollTriggerRef.current.kill();
    }

    scrollTriggerRef.current = ScrollTrigger.create({
      id: 'residence-tiers-id',
      trigger: '#residence-tiers',
      start: '=2% top',
      end: 'bottom bottom',
      scrub: true,
      // markers:true,
      onEnter: self => {
        const progress = self.progress;
        const titleContent = progress > 0.9 
          ? '<span>奥运冠军 <span style="background-color:white; border-bottom:1px solid black;">现居地</span> 类型</span>' 
          : '<span style="transform:scale(0.7);">奥运冠军 <span style="background-color:white; border-bottom:1px solid black;">家乡</span> 城市类型</span>';

        const titleElement = document.querySelector('.chart-title span');
        if (titleElement) {
          titleElement.innerHTML = titleContent;
        }

        backgroundRect.attr('opacity', progress > 0.5 ? '0.5' : '0');

        circles.each(function(d) {
          d3.select(this)
            .transition()
            .duration(1500)
            .delay(100)
            .attr('cx', d => d.initialX + progress * (d.finalX - d.initialX))
            .attr('cy', d => d.initialY + progress * (d.finalY - d.initialY));
        });
      },
      onLeave: self => {
        backgroundRect.attr('opacity', '0.5');
        circles.each(function(d) {
          d3.select(this)
            .transition()
            .duration(1500)
            .attr('cx', d => d.finalX)
            .attr('cy', d => d.finalY);
        });
      },
      onLeaveBack: self => {
        const progress = self.progress;
        const titleContent = progress > 0.9 
          ? '<span>奥运冠军 <span style="background-color:white; border-bottom:1px solid black;">现居地</span> 类型</span>' 
          : '<span>奥运冠军 <span style="background-color:white; border-bottom:1px solid black;">家乡</span> 城市类型</span>';

        const titleElement = document.querySelector('.chart-title span');
        if (titleElement) {
          titleElement.innerHTML = titleContent;
        }

        backgroundRect.attr('opacity', progress > 0.9 ? '0.5' : '0');

        circles.each(function(d) {
          d3.select(this)
            .transition()
            .duration(1500)
            .attr('cx', d => d.initialX + progress * (d.finalX - d.initialX))
            .attr('cy', d => d.initialY + progress * (d.finalY - d.initialY));
        });
      }
    });

  }, [color, processedData]);

  useEffect(() => {
    const handleResize = debounce(() => {
      drawChart();
    }, 250);

    drawChart();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (scrollTriggerRef.current) {
        scrollTriggerRef.current.kill();
      }
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [drawChart, debounce]);

  return (
    <div>
      
      <div className='content'>
      
        <div className='content__text'>
        <h2>二. 奥运冠军退役后，更有可能在一二线城市发展，或移民海外</h2>

        <p>在中国城市化进程发展的大背景下, 绝大多数的中国奥运健儿拿到金牌后, 也实现了户籍向上跃迁。下图记录对比了奥运冠军的出生地和现居地，图中可见一个显著的左向移动。他们大多来自中国三、四五线城市，北京体育官员此前在接受采访<a href='https://cn.nytimes.com/china/20210730/china-olympics/' target='_blank'>时说</a>,“农村来的孩子或者家庭条件稍微不是那么好的那种情况下的那种孩子，他们吃苦耐劳的能力更强”。退役以后，奥运冠军绝大多数都生活在中国二线城市及以上。</p>
        
      
        </div>
      </div>
        
        <div ref= {containerRef} className='chart-container'>
        
        <div className='chart-title sticky-div-wrapper' style={{ zIndex:10 }}>
          <span>奥运冠军 <span style={{ backgroundColor:'white', borderBottom:'1px solid black' }}>家乡</span> 城市类型</span>
        </div>
          
          <div className='content__text'>
            <div className='legend' >
            {sex.map((i) => (
              <div key={i} className='legend__item'>
                <div className='legend__color round' style={{ backgroundColor:color(i) }}></div>
                <div className='legend__label'>{i}</div>
                </div>
            ))}
          </div>
          </div>
          
            <svg ref={svgRef} id='residence-tiers' xmlns="http://www.w3.org/2000/svg" version="1.1"></svg>
            <Tooltip content={tooltipContent} position={tooltipPosition} hovered={hovered} />
        </div>
      

      <div className='content'>
      <div className='scroll__caption chart-caption'>图注：将鼠标悬浮图表圆圈，可查看每个退役冠军具体信息。</div>
        <div className='content__text'>
        <p>
        射击、羽毛球、乒乓球、排球、跳水、举重和体操是中国传统奥运会金牌常客。歪脑数据统计，在这7个项目中，体操奥运冠军实现了最大程度的户籍跃迁。相比之下，举重运动员的出路更为艰难。二十多年前，<a href='https://www.sohu.com/a/217946840_653748' target='_blank'>《一个举重冠军之死》</a>描绘了前亚运会举重冠军退役后做门卫，无钱看病身亡的潦倒。有媒体<a href='https://www.thepaper.cn/newsDetail_forward_13899728' target='_blank'>写道</a>，“相比于伤病，变现难度大和职业化程度低才是导致他们贫困最主要的原因。
          </p>
        </div>

        <ImageScroll
        src1='/images/AP04082104660.jpg'
        caption1='2004年8月21日，中国选手唐功红在雅典夏季奥林匹克运动会尼凯亚举重馆举行的女子超过165磅（+75公斤）级比赛中，进行了挺举。唐功红刷新了世界纪录，赢得金牌。(AP / Charles Krupa)'
        src2='/images/tanggonghong-douyin.jpg'
        caption2='2023年8月，唐功红在抖音发布的与儿子的合影。（抖音 / 唐功红）'
        />


      </div>
    </div>
  );
};

export default ResidenceTiers;